import { graphql } from 'gql.tada';
import { useQuery } from '@apollo/client';
import type { ResultOf } from 'gql.tada';

const ChecklistStepQuery = graphql.persisted(
  "ChecklistStep",
  graphql(`
    query ChecklistStep($schoolId: ID!, $itemId: ID!) {
      checklistStep(schoolId: $schoolId, itemId: $itemId) {
        item { id title stepKey stepType state completedAt lockedReason keptAsItIs }
        yearName
        yearSlug
        intro
        answers
        who {
          children { studentEnrollmentId name grade returning state }
          programs
          plans
          aidRequests
          allowNewChildren
          acceptOffer
          grades
          canChange
        }
        household { fields }
        childDetails { fields childName returning }
        consent {
          answer
          intro
          wording
          questions { key text }
          choices { key text }
          documents { name url label }
          typedName
          childName
        }
        todo { details }
        agreement {
          agreementId
          plan { key name scheduleType }
          children { name programName costCents partYearCents startsOn shareOfYear }
          discountCents
          aidCents
          totalCents
          payments { dueOn amountCents autoPay lines { description amountCents } }
          isChange
          kept { dueOn amountCents status }
          terms { intro terms }
          schoolYear
          signed
          planCanChange
          whoItemId
          programsMissing
        }
        autoPay {
          intro
          wording
          paymentMethods { paymentMethodId brand last4 type }
          paymentMethodsError
          activeMethod { paymentMethodId brand last4 type }
        }
        fee {
          amountCentsPerChild
          children { name amountCents }
          totalCents
          invoiceId
          status
          paidAt
        }
      }
    }
  `)
);

type ChecklistStepResult = NonNullable<ResultOf<typeof ChecklistStepQuery>>;
export type ChecklistStepView = NonNullable<ChecklistStepResult['checklistStep']>;
export type WhoContext = NonNullable<ChecklistStepView['who']>;
export type HouseholdContext = NonNullable<ChecklistStepView['household']>;
export type ChildDetailsContext = NonNullable<ChecklistStepView['childDetails']>;
export type ConsentContext = NonNullable<ChecklistStepView['consent']>;
export type TodoContext = NonNullable<ChecklistStepView['todo']>;
export type AgreementContext = NonNullable<ChecklistStepView['agreement']>;
export type AutoPayContext = NonNullable<ChecklistStepView['autoPay']>;
export type FeeContext = NonNullable<ChecklistStepView['fee']>;

export { ChecklistStepQuery };

/**
 * Always from the server when the page opens: the step's answers are what the
 * page saves back whole, so a copy cached from an earlier visit would undo
 * what was saved since.
 */
export function useChecklistStep(schoolId?: string, itemId?: string) {
  return useQuery(ChecklistStepQuery, {
    variables: { schoolId: schoolId!, itemId: itemId! },
    skip: !schoolId || !itemId,
    fetchPolicy: 'network-only',
  });
}
